import { useState } from 'react';
import { Link, useRoute, useLocation } from 'wouter';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { getDateLocale, getDateFormats } from '@/utils/dateLocale';
import { ArrowLeft, Calendar, User, Users, ChevronRight, Layers } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { bookingService } from '@/services/api/booking.service';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { StatusBadge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { useCurrency } from '@/hooks/useCurrency';
import { EditBookingDialog } from '@/components/bookings/EditBookingDialog';
import { RescheduleBookingDialog } from '@/components/bookings/RescheduleBookingDialog';
import { EditBatchDialog } from '@/components/bookings/EditBatchDialog';
import type { BookingStatus } from '@/lib/status-colors';
import { SessionNoteCard } from '@/components/sessions/SessionNoteCard';

export default function AdminBookingDetailsPage() {
  const { t } = useTranslation(['bookings', 'common']);
  const [, params] = useRoute('/admin/bookings/:id');
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const { formatCurrency } = useCurrency();
  const queryClient = useQueryClient();

  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showRescheduleDialog, setShowRescheduleDialog] = useState(false);
  const [showEditBatchDialog, setShowEditBatchDialog] = useState(false);

  // Accept both UUID and numeric ID (for backward compatibility)
  const bookingId = (params as { id?: string })?.id || null;

  // Fetch booking details
  const { data: booking, isLoading, error } = useQuery({
    queryKey: ['admin-booking', bookingId],
    queryFn: () => bookingService.getAdminBooking(bookingId!),
    enabled: !!bookingId,
  });

  // Confirm booking mutation
  const confirmBookingMutation = useMutation({
    mutationFn: (id: number) => bookingService.confirmBooking(id),
    onSuccess: () => {
      toast({ title: t('common:toasts.updateSuccess'), description: t('bookings:toasts.confirmSuccess') });
      queryClient.invalidateQueries({ queryKey: ['admin-booking', bookingId] });
      queryClient.invalidateQueries({ queryKey: ['admin-bookings'] });
    },
    onError: (error: any) => {
      toast({
        title: t('common:errors.generic'),
        description: error.message || t('bookings:toasts.confirmError'),
        variant: 'destructive',
      });
    },
  });

  // Cancel booking mutation
  const cancelBookingMutation = useMutation({
    mutationFn: (id: number) => bookingService.cancelBooking(id, t('bookings:details.cancelledByStaff')),
    onSuccess: () => {
      toast({ title: t('common:toasts.updateSuccess'), description: t('bookings:toasts.cancelSuccess') });
      queryClient.invalidateQueries({ queryKey: ['admin-booking', bookingId] });
      queryClient.invalidateQueries({ queryKey: ['admin-bookings'] });
    },
    onError: (error: any) => {
      toast({
        title: t('common:errors.generic'),
        description: error.message || t('bookings:toasts.cancelError'),
        variant: 'destructive',
      });
    },
  });

  // Cancel every future booking in the batch this booking belongs to.
  const cancelSeriesMutation = useMutation({
    mutationFn: ({ seriesId, customerEmail }: { seriesId: string; customerEmail: string }) =>
      bookingService.cancelSeries(seriesId, customerEmail),
    onSuccess: (result) => {
      toast({
        title: t('common:toasts.updateSuccess'),
        description: t('bookings:batch.cancelSuccess', {
          defaultValue: '{{count}} bookings cancelled',
          count: result.cancelled_count,
        }),
      });
      queryClient.invalidateQueries({ queryKey: ['admin-booking', bookingId] });
      queryClient.invalidateQueries({ queryKey: ['admin-bookings'] });
    },
    onError: (error: any) => {
      toast({
        title: t('common:errors.generic'),
        description: error.message || t('bookings:batch.cancelError', 'Failed to cancel the batch'),
        variant: 'destructive',
      });
    },
  });

  // Cancel the whole series for EVERY customer in it (unscoped). Only offered
  // when the series spans more than one customer.
  const cancelWholeSeriesMutation = useMutation({
    mutationFn: (seriesId: string) => bookingService.cancelSeries(seriesId),
    onSuccess: (result) => {
      toast({
        title: t('common:toasts.updateSuccess'),
        description: t('bookings:batch.cancelSeriesSuccess', {
          defaultValue: '{{count}} bookings cancelled across the series',
          count: result.cancelled_count,
        }),
      });
      queryClient.invalidateQueries({ queryKey: ['admin-booking', bookingId] });
      queryClient.invalidateQueries({ queryKey: ['admin-bookings'] });
    },
    onError: (error: any) => {
      toast({
        title: t('common:errors.generic'),
        description: error.message || t('bookings:batch.cancelError', 'Failed to cancel the batch'),
        variant: 'destructive',
      });
    },
  });

  const handleConfirm = () => {
    if (booking && confirm(t('bookings:dialogs.confirmBooking'))) {
      confirmBookingMutation.mutate(booking.id);
    }
  };

  const handleCancelBatch = () => {
    const seriesId = booking?.bookingSeriesId;
    const count = booking?.series?.futureCount ?? 0;
    if (!seriesId || !booking) return;
    if (confirm(t('bookings:batch.cancelConfirm', {
      defaultValue: 'Cancel all {{count}} upcoming bookings for this customer in the batch?',
      count,
    }))) {
      // Scope to this participant — never cancel co-participants from one booking.
      cancelSeriesMutation.mutate({ seriesId, customerEmail: booking.customerEmail });
    }
  };

  const handleCancel = () => {
    const message = isGroupParticipant
      ? t('bookings:dialogs.removeFromClass', 'Remove this participant from the class? Their booking for this class is cancelled and they\'ll be emailed. Any other bookings are unaffected.')
      : t('bookings:dialogs.cancelBooking');
    if (booking && confirm(message)) {
      cancelBookingMutation.mutate(booking.id);
    }
  };

  const handleCancelWholeSeries = () => {
    const seriesId = booking?.bookingSeriesId;
    const count = booking?.series?.customerCount ?? 0;
    if (!seriesId) return;
    if (confirm(t('bookings:batch.cancelSeriesConfirm', {
      defaultValue: 'This cancels every upcoming booking for all {{count}} customers in this series. This cannot be undone.',
      count,
    }))) {
      cancelWholeSeriesMutation.mutate(seriesId);
    }
  };

  // Group-class booking → "Cancel booking" actually means "remove from this class".
  const slotCap = (booking?.availableSlot as any)?.maxCapacity
    ?? (booking?.service as any)?.maxCapacity
    ?? (booking?.service as any)?.max_capacity
    ?? 1;
  const isGroupParticipant = slotCap > 1;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600 mx-auto"></div>
          <p className="mt-4 text-soft">{t('bookings:loading')}</p>
        </div>
      </div>
    );
  }

  if (error || !booking) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            onClick={() => setLocation('/admin/bookings')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('common:back')}
          </Button>
        </div>
        <Card>
          <CardContent className="p-12 text-center">
            <p className="text-danger mb-4">{t('bookings:errors.notFound')}</p>
            <Button onClick={() => setLocation('/admin/bookings')}>
              {t('bookings:backToList')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-h1 text-ink">
              {t('bookings:details.title')}
            </h1>
            <p className="text-body-sm text-soft font-mono">
              {booking.bookingCode || booking.booking_code}
            </p>
          </div>
          <StatusBadge status={booking.status as BookingStatus} className="shrink-0">
            {t(`bookings:status.${booking.status === 'no_show' ? 'noShow' : booking.status}`)}
          </StatusBadge>
        </div>

        <Button
          variant="ghost"
          size="sm"
          onClick={() => setLocation('/admin/bookings')}
          iconLeft={<ArrowLeft />}
        >
          {t('common:back')}
        </Button>
      </div>

      {/* Group-class banner — links to the full class with all participants */}
      {(() => {
        const slot = booking.availableSlot as any;
        const cap = slot?.maxCapacity ?? (booking.service as any)?.maxCapacity ?? 1;
        if (cap > 1 && slot?.id) {
          return (
            <button
              onClick={() => setLocation(`/admin/group-classes/${slot.id}`)}
              className="w-full flex items-center justify-between gap-3 rounded-lg border border-line bg-surface-2 px-4 py-3 hover:bg-surface-2/70 transition-colors text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
            >
              <div className="flex items-center gap-3 min-w-0">
                <Users className="h-5 w-5 text-brand-500 flex-shrink-0" />
                <div className="min-w-0">
                  <div className="text-label text-ink truncate">
                    {t('staff:availability.groupClass.review.title', 'Group class')} · {slot.currentBookings ?? 0}/{cap}
                  </div>
                  <div className="text-body-xs text-soft">
                    {t('staff:availability.groupClass.openFullPage', 'Open full page')}
                  </div>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-soft flex-shrink-0" />
            </button>
          );
        }
        return null;
      })()}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <SessionNoteCard bookingId={booking.publicId ?? booking.id} summary={booking.sessionNote} />
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  {t('bookings:details.customerInfo')}
                </span>
                {booking.customerId != null && (
                  <Link
                    href={`/admin/customers/${booking.customerId}`}
                    className="inline-flex items-center gap-1 text-sm font-medium text-brand-600 hover:underline"
                  >
                    {t('bookings:details.viewCustomer', 'View customer')}
                    <ChevronRight className="h-4 w-4" />
                  </Link>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-mono-eyebrow uppercase text-soft">{t('bookings:details.fields.name')}</p>
                  <p className="text-base">{booking.customerFirstName} {booking.customerLastName}</p>
                </div>
                <div>
                  <p className="text-mono-eyebrow uppercase text-soft">{t('bookings:details.fields.email')}</p>
                  <p className="text-base break-all">{booking.customerEmail}</p>
                </div>
                {booking.customerPhone && (
                  <div>
                    <p className="text-mono-eyebrow uppercase text-soft">{t('bookings:details.fields.phone')}</p>
                    <p className="text-base">{booking.customerPhone}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                {t('bookings:details.appointmentInfo')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-mono-eyebrow uppercase text-soft">{t('bookings:details.fields.service')}</p>
                  <p className="text-base">{booking.service?.name || booking.availableSlot?.service?.name}</p>
                </div>
                <div>
                  <p className="text-mono-eyebrow uppercase text-soft">{t('bookings:details.fields.provider')}</p>
                  <p className="text-base">{booking.provider?.name || (booking.availableSlot?.provider as any)?.displayName}</p>
                </div>
                <div>
                  <p className="text-mono-eyebrow uppercase text-soft">{t('bookings:details.fields.date')}</p>
                  <p className="text-base">
                    {booking.start_time
                      ? format(parseISO(booking.start_time), getDateFormats().fullWithWeekday, { locale: getDateLocale() })
                      : booking.availableSlot?.startTime && format(parseISO(booking.availableSlot.startTime), getDateFormats().fullWithWeekday, { locale: getDateLocale() })
                    }
                  </p>
                </div>
                <div>
                  <p className="text-mono-eyebrow uppercase text-soft">{t('bookings:details.fields.time')}</p>
                  <p className="text-base">
                    {booking.start_time && booking.end_time
                      ? `${format(parseISO(booking.start_time), 'HH:mm')} - ${format(parseISO(booking.end_time), 'HH:mm')}`
                      : booking.availableSlot?.startTime && booking.availableSlot?.endTime &&
                        `${format(parseISO(booking.availableSlot.startTime), 'HH:mm')} - ${format(parseISO(booking.availableSlot.endTime), 'HH:mm')}`
                    }
                  </p>
                </div>
                <div>
                  <p className="text-mono-eyebrow uppercase text-soft">{t('bookings:details.fields.duration')}</p>
                  <p className="text-base">
                    {booking.service?.duration_minutes || booking.service?.durationMinutes || 'N/A'} {t('bookings:details.minutes')}
                  </p>
                </div>
                <div>
                  <p className="text-mono-eyebrow uppercase text-soft">{t('bookings:details.fields.price')}</p>
                  <p className="text-base">
                    {booking.service?.price ? formatCurrency(booking.service.price) : 'N/A'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Notes */}
          {booking.notes && (
            <Card>
              <CardHeader>
                <CardTitle>{t('bookings:details.notes')}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-body text-ink bg-surface-2 p-3 rounded-lg">
                  {booking.notes}
                </p>
              </CardContent>
            </Card>
          )}

          {/* Custom Questions & Answers */}
          {booking.custom_answers && Object.keys(booking.custom_answers).length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>{t('common:labels.customQuestions')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="bg-surface-2 border border-line rounded-lg p-4 space-y-3">
                  {Object.entries(booking.custom_answers).map(([questionId, answer]) => {
                    const service = booking.service || booking.availableSlot?.service;
                    const customQuestions = service?.custom_booking_questions || service?.customBookingQuestions || [];
                    const question = customQuestions.find((q: any) => q.id === questionId);

                    if (!question) {
                      return null;
                    }

                    const questionText = question.question;
                    const displayAnswer = Array.isArray(answer) ? answer.join(', ') : answer;
                    return (
                      <div key={questionId} className="border-b border-line last:border-0 pb-2 last:pb-0">
                        <p className="text-mono-eyebrow uppercase text-soft">{questionText}</p>
                        <p className="text-body text-ink">{displayAnswer}</p>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>{t('bookings:details.bookingDetails')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div>
                  <p className="text-mono-eyebrow uppercase text-soft">{t('bookings:details.fields.bookingId')}</p>
                  <p className="text-base font-mono">{booking.bookingCode || booking.booking_code}</p>
                </div>
                <div>
                  <p className="text-mono-eyebrow uppercase text-soft">{t('bookings:details.fields.status')}</p>
                  <StatusBadge status={booking.status as BookingStatus}>
                    {t(`bookings:status.${booking.status === 'no_show' ? 'noShow' : booking.status}`)}
                  </StatusBadge>
                </div>
                <div>
                  <p className="text-mono-eyebrow uppercase text-soft">{t('bookings:details.fields.created')}</p>
                  <p className="text-base">{format(parseISO(booking.createdAt), getDateFormats().medium, { locale: getDateLocale() })} {format(parseISO(booking.createdAt), 'HH:mm')}</p>
                </div>
                {booking.confirmedAt && (
                  <div>
                    <p className="text-mono-eyebrow uppercase text-soft">{t('bookings:details.fields.confirmed')}</p>
                    <p className="text-base">{format(parseISO(booking.confirmedAt), getDateFormats().medium, { locale: getDateLocale() })} {format(parseISO(booking.confirmedAt), 'HH:mm')}</p>
                  </div>
                )}
                {booking.cancelledAt && (
                  <div>
                    <p className="text-mono-eyebrow uppercase text-soft">{t('bookings:details.fields.cancelled')}</p>
                    <p className="text-base">{format(parseISO(booking.cancelledAt), getDateFormats().medium, { locale: getDateLocale() })} {format(parseISO(booking.cancelledAt), 'HH:mm')}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t('bookings:actions.title')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {['pending', 'confirmed'].includes(booking.status) && (
                <Button
                  variant="outline"
                  onClick={() => setShowEditDialog(true)}
                  className="w-full"
                >
                  {t('bookings:actions.editDetails', 'Edit Details')}
                </Button>
              )}
              {['pending', 'confirmed'].includes(booking.status) && (
                <Button
                  variant="outline"
                  onClick={() => setShowRescheduleDialog(true)}
                  className="w-full"
                >
                  {t('bookings:actions.reschedule', 'Reschedule')}
                </Button>
              )}
              {booking.status === 'pending' && (
                <Button
                  variant="success"
                  onClick={handleConfirm}
                  className="w-full"
                  disabled={confirmBookingMutation.isPending}
                >
                  {t('bookings:actions.confirmBooking')}
                </Button>
              )}
              {['pending', 'confirmed'].includes(booking.status) && (
                <Button
                  variant="destructive"
                  onClick={handleCancel}
                  className="w-full"
                  disabled={cancelBookingMutation.isPending}
                >
                  {isGroupParticipant
                    ? t('bookings:actions.removeFromClass', 'Cancel booking (remove from class)')
                    : t('bookings:actions.cancelBooking')}
                </Button>
              )}

              {/* Batch actions — only when this booking is part of a series with
                  upcoming bookings. Acts on the whole batch, not just this one. */}
              {booking.bookingSeriesId && (booking.series?.futureCount ?? 0) > 0 && (
                <div className="space-y-2 pt-2 mt-2 border-t border-line">
                  <p className="flex items-center gap-2 text-mono-eyebrow uppercase text-soft">
                    <Layers className="h-4 w-4" />
                    {t('bookings:batch.sectionTitle', 'Batch')}
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => setShowEditBatchDialog(true)}
                    className="w-full"
                  >
                    {t('bookings:batch.edit', {
                      defaultValue: 'Edit batch ({{count}} slots)',
                      count: booking.series?.futureCount ?? 0,
                    })}
                  </Button>
                  <Button
                    variant="outline-danger"
                    onClick={handleCancelBatch}
                    className="w-full"
                    disabled={cancelSeriesMutation.isPending}
                  >
                    {t('bookings:batch.cancelAll', {
                      defaultValue: 'Cancel all upcoming ({{count}})',
                      count: booking.series?.futureCount ?? 0,
                    })}
                  </Button>
                  {(booking.series?.customerCount ?? 0) > 1 && (
                    <Button
                      variant="outline-danger"
                      onClick={handleCancelWholeSeries}
                      className="w-full"
                      disabled={cancelWholeSeriesMutation.isPending}
                    >
                      {t('bookings:batch.cancelSeriesEveryone', {
                        defaultValue: 'Cancel entire series ({{count}} customers)',
                        count: booking.series?.customerCount ?? 0,
                      })}
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Dialogs */}
      <EditBookingDialog
        booking={booking}
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
        bookingId={bookingId}
      />
      <RescheduleBookingDialog
        booking={booking}
        open={showRescheduleDialog}
        onOpenChange={setShowRescheduleDialog}
        bookingId={bookingId}
      />
      {booking.bookingSeriesId && (
        <EditBatchDialog
          seriesId={booking.bookingSeriesId}
          customerEmail={booking.customerEmail}
          count={booking.series?.futureCount ?? 0}
          open={showEditBatchDialog}
          onOpenChange={setShowEditBatchDialog}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['admin-booking', bookingId] });
            queryClient.invalidateQueries({ queryKey: ['admin-bookings'] });
          }}
        />
      )}
    </div>
  );
}
